/**
 * Client for the Kyoto workflow-recording compile endpoint.
 *
 * Posts the captured recording bundle (meta JSON + audio + sampled frames) as
 * multipart/form-data, then drains the server-sent-events response stream,
 * dispatching phase/result/error events to the supplied handlers.
 *
 * SSE delivery semantics:
 *   - "phase"  → onPhase(data)
 *   - "result" → onResult(data)
 *   - "error"  → onError(data); the stream is terminated. Any subsequent
 *                events (including a stale "result") are suppressed. The
 *                reader is cancelled so the underlying fetch body can be
 *                released.
 *
 * The terminated flag is local to each compile() invocation so concurrent
 * calls don't poison each other.
 */
import { BASE, csrfToken, authHeader } from "../../apiClient";
import { consumeSseStream } from "./sseClient";

export class CompileHttpError extends Error {
  constructor(status, body, retryAfter) {
    super(`compile failed (${status})`);
    this.status = status;
    this.body = body;
    this.retryAfter = retryAfter;
  }
}

function audioFilename(audioMime) {
  return audioMime && audioMime.includes("mp4") ? "audio.m4a" : "audio.webm";
}

function buildBody(bundle) {
  const fd = new FormData();
  const metaBlob = new Blob([JSON.stringify(bundle.meta)], {
    type: "application/json",
  });
  fd.append("meta", metaBlob, "meta.json");
  if (bundle.audioBlob) {
    fd.append("audio", bundle.audioBlob, audioFilename(bundle.meta?.audioMime));
  }
  // A client-supplied transcript (from a prior /transcribe call) lets Kyoto
  // skip Whisper. Absent → Kyoto transcribes the audio itself.
  if (bundle.transcript) {
    const transcriptBlob = new Blob([JSON.stringify(bundle.transcript)], {
      type: "application/json",
    });
    fd.append("transcript", transcriptBlob, "transcript.json");
  }
  for (let i = 0; i < bundle.frames.length; i++) {
    const f = bundle.frames[i];
    fd.append(`frame_${i}`, f.blob, `frame_${i}_t${f.t}.jpg`);
  }
  return fd;
}

export async function compile({ bundle, onPhase, onResult, onError, signal }) {
  const res = await fetch(`${BASE}/workflows/compile`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRFToken": csrfToken(), ...authHeader() },
    body: buildBody(bundle),
    signal,
  });

  const contentType = res.headers.get("Content-Type") || "";
  if (!res.ok || !contentType.includes("text/event-stream")) {
    let body = null;
    try { body = await res.json(); } catch (_) {}
    throw new CompileHttpError(res.status, body, res.headers.get("Retry-After"));
  }

  let terminated = false;
  const reader = res.body.getReader();
  await consumeSseStream(reader, {
    phase: (data) => {
      if (terminated) return;
      onPhase(data);
    },
    result: (data) => {
      if (terminated) return;
      onResult(data);
    },
    error: async (data) => {
      if (terminated) return;
      terminated = true;
      onError(data);
      await reader.cancel().catch(() => {});
    },
  });
}
